'use client';

/**
 * The chips under the composer: the skill attached to the next message, and the
 * files already uploaded for it. Both are removable so a mis-pick doesn't have
 * to be sent.
 *
 * These render BELOW the textarea and never wrap it — keeping the field's
 * position in the tree fixed is what stops a badge appearing mid-type from
 * remounting a focused input (TYPING_FOCUS_STABILITY).
 */

import React from 'react';
import { X } from 'lucide-react';
import type { FilePart } from '../../../lib/db/types';
import type { AttachedSkill } from '../../skills/types';

const BADGE_BASE = 'inline-flex max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-xs text-foreground';

export function AttachedSkillBadge({
  skill,
  onClear,
}: {
  skill: AttachedSkill;
  onClear?: () => void;
}) {
  return (
    <div className="flex flex-wrap gap-2 px-1 pt-2">
      <span
        className={`${BADGE_BASE} border border-primary/30 bg-primary/10`}
        data-testid="attached-skill-badge"
      >
        <span className="truncate font-medium" title={skill.description}>
          Skill: {skill.id}
        </span>
        {onClear && (
          <button
            type="button"
            onClick={onClear}
            className="shrink-0 rounded-full p-0.5 hover:bg-primary/20"
            aria-label={`Remove skill ${skill.id}`}
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        )}
      </span>
    </div>
  );
}

export function AttachedFileBadges({
  files,
  onRemove,
}: {
  files: readonly FilePart[];
  onRemove: (fileId: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2 px-1 pt-2" data-testid="composer-attachments">
      {files.map((file) => (
        <span key={file.fileId} className={`${BADGE_BASE} border border-border bg-muted/50`}>
          <span className="truncate" title={file.filename}>
            {file.filename}
          </span>
          <button
            type="button"
            onClick={() => onRemove(file.fileId)}
            className="shrink-0 rounded-full p-0.5 hover:bg-muted"
            aria-label={`Remove ${file.filename}`}
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </span>
      ))}
    </div>
  );
}
